import React, { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
    Inbox, Users, Settings, BarChart3, Calendar, FileText, Megaphone,
    LineChart, Activity, LogOut, Sparkles
} from 'lucide-react';

interface NavItem {
    path: string;
    label: string;
    icon: React.ReactNode;
    phase?: string;
}

const NAV: NavItem[] = [
    { path: '/admin/leady', label: 'Leady', icon: <Users className="w-5 h-5" /> },
    { path: '/admin/posty', label: 'Posty FB+IG', icon: <Megaphone className="w-5 h-5" /> },
    { path: '/admin/aktywnosc', label: 'Aktywność live', icon: <Activity className="w-5 h-5" /> },
    { path: '/admin/inbox', label: 'Inbox (rozmowy)', icon: <Inbox className="w-5 h-5" /> },
    { path: '/admin/settings', label: 'Settings (LLM)', icon: <Settings className="w-5 h-5" /> },
    { path: '/admin/mediaplan', label: 'Mediaplan', icon: <Calendar className="w-5 h-5" />, phase: 'Phase 3' },
    { path: '/admin/harmonogram', label: 'Harmonogram', icon: <Calendar className="w-5 h-5" />, phase: 'Phase 3' },
    { path: '/admin/propozycje-postow', label: 'Propozycje postów', icon: <FileText className="w-5 h-5" />, phase: 'Phase 3' },
    { path: '/admin/reklamy', label: 'Reklamy', icon: <Megaphone className="w-5 h-5" />, phase: 'Phase 4' },
    { path: '/admin/ga4', label: 'GA4', icon: <BarChart3 className="w-5 h-5" />, phase: 'Phase 3' },
    { path: '/admin/gsc', label: 'GSC', icon: <LineChart className="w-5 h-5" />, phase: 'Phase 3' },
    { path: '/admin/analytics', label: 'Analytics', icon: <Activity className="w-5 h-5" />, phase: 'Phase 5' },
];

export const AdminLayout: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [authed, setAuthed] = useState<boolean | null>(null);

    useEffect(() => {
        // Check session by hitting /api/admin/leads — if 401, send to login
        fetch('/api/admin/leads?limit=1', { credentials: 'include' })
            .then(r => {
                if (r.status === 401) {
                    setAuthed(false);
                    if (location.pathname !== '/admin/login') navigate('/admin/login');
                } else {
                    setAuthed(true);
                }
            })
            .catch(() => setAuthed(false));
    }, [navigate, location.pathname]);

    const onLogout = async () => {
        await fetch('/api/admin-auth/logout', { method: 'POST', credentials: 'include' });
        navigate('/admin/login');
    };

    // Login page renders without sidebar
    if (location.pathname === '/admin/login' || authed === false && location.pathname === '/admin/login') {
        return <Outlet />;
    }

    if (authed === null) {
        return <div className="min-h-screen flex items-center justify-center text-text-secondary-light dark:text-text-secondary-dark">Ładowanie…</div>;
    }

    return (
        <div className="min-h-screen flex bg-bkg-light dark:bg-bkg-dark">
            <aside className="w-64 border-r border-border-light dark:border-border-dark p-4 flex flex-col gap-1">
                <Link to="/admin/leady" className="flex items-center gap-2 mb-6 px-2">
                    <Sparkles className="w-6 h-6 text-primary-light dark:text-primary-dark" />
                    <span className="font-bold">BBK Admin</span>
                </Link>
                <nav className="flex-1 space-y-1 overflow-y-auto">
                    {NAV.map(item => (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            className={({ isActive }) =>
                                `flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                                    isActive
                                        ? 'bg-primary-light/10 dark:bg-primary-dark/10 text-primary-light dark:text-primary-dark font-semibold'
                                        : 'text-text-secondary-light dark:text-text-secondary-dark hover:bg-content-light dark:hover:bg-content-dark'
                                }`
                            }
                        >
                            <span className="flex items-center gap-2">
                                {item.icon}
                                {item.label}
                            </span>
                            {item.phase && (
                                <span className="text-[10px] uppercase tracking-wider text-text-secondary-light/70 dark:text-text-secondary-dark/70">
                                    {item.phase}
                                </span>
                            )}
                        </NavLink>
                    ))}
                </nav>
                <button
                    onClick={onLogout}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-red-500 hover:bg-red-500/10"
                >
                    <LogOut className="w-4 h-4" /> Wyloguj
                </button>
            </aside>
            <main className="flex-1 p-6 overflow-auto">
                <Outlet />
            </main>
        </div>
    );
};
